import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Code2, Package, Tag, Key, Zap, FileCode, ChevronRight, Copy, Check, Info, Shield, Server } from 'lucide-react';
import { QUOTE_URL } from '../lib/links';


const PREALERT_EXAMPLE = `// Headers
{
  "Content-Type": "application/json",
  "x-api-key": "<PROVIDED_BY_SALES_TEAM>"
}

// Body
{
  "name": "Name LastName",
  "email": "example@example.com",
  "Warehouse": "Miami",
  "FM_carrier": "FEDEX",
  "tracking_FM": "01010101",
  "ETA": "2025-10-08",
  "date": "2025-10-08",
  "tracking_numbers": [
    "MLAR000000000EX",
    "MLPE00000000EX",
    "MLMX000000000EX"
  ]
}`;

// Business rules: "`field`" in a translated text is shown as code
const AnnexItems = ({ items }) =>
  (Array.isArray(items) ? items : []).map(({ k, v }) => (
    <li key={k}>
      • <span className="text-white">{k}:</span>{' '}
      {v.split('`').map((part, i) => (i % 2 ? <code key={part} className="text-[#7AC142]">{part}</code> : part))}
    </li>
  ));

const ApiDocsSection = () => {
  const { t, language } = useLanguage();
  const [activeApi, setActiveApi] = useState('label');
  const [activeLabelGroup, setActiveLabelGroup] = useState('general');
  const [copied, setCopied] = useState(false);

  // Field descriptions translated
  const D = {
    // Pre-Alert
    paName: { en: 'Full name of the operational contact for this shipment.', es: 'Nombre completo del contacto operacional para este envío.', pt: 'Nome completo do contato operacional deste envio.', zh: '本次发货的运营联系人全名。' },
    paEmail: { en: 'Email address of the operational contact responsible for this shipment.', es: 'Email del contacto operacional responsable de este envío.', pt: 'Email do contato operacional responsável por este envio.', zh: '负责本次发货的运营联系人邮箱。' },
    paWarehouse: { en: 'Warehouse identifier. Must be capitalized — e.g. Miami, Los Angeles, Chicago, New Jersey.', es: 'Identificador del almacén. Debe ir capitalizado — ej. Miami, Los Angeles, Chicago, New Jersey.', pt: 'Identificador do armazém. Deve estar capitalizado — ex. Miami, Los Angeles, Chicago, New Jersey.', zh: '仓库标识符。必须首字母大写——例如 Miami、Los Angeles、Chicago、New Jersey。' },
    paFMcarrier: { en: 'Name of the carrier handling the shipment.', es: 'Nombre del courier que maneja el envío.', pt: 'Nome do courier que manuseia o envio.', zh: '负责承运本次发货的承运商名称。' },
    paTrackingFM: { en: 'Carrier-assigned tracking number for this shipment.', es: 'Número de tracking asignado por el courier para este envío.', pt: 'Número de tracking atribuído pelo courier para este envio.', zh: '承运商为本次发货分配的追踪号。' },
    paETA: { en: 'Estimated date of arrival at the Warehouse. Format: YYYY-MM-DD.', es: 'Fecha estimada de arribo al almacén. Formato: YYYY-MM-DD.', pt: 'Data estimada de chegada ao armazém. Formato: YYYY-MM-DD.', zh: '预计到达仓库的日期。格式:YYYY-MM-DD。' },
    paDate: { en: 'Shipment dispatch date. Format: YYYY-MM-DD.', es: 'Fecha de despacho del envío. Formato: YYYY-MM-DD.', pt: 'Data de despacho do envio. Formato: YYYY-MM-DD.', zh: '发货日期。格式:YYYY-MM-DD。' },
    paTrackingNumbers: { en: 'Array of individual package tracking numbers included in this shipment. Accepts multiple values.', es: 'Array de tracking numbers individuales de los paquetes en este envío. Acepta múltiples valores.', pt: 'Array de tracking numbers individuais dos pacotes deste envio. Aceita múltiplos valores.', zh: '本次发货中所包含的各个包裹追踪号数组。接受多个值。' },

    // Label - General
    orderId: { en: 'Seller Order ID / Reference.', es: 'ID/Referencia de Orden del Seller.', pt: 'ID/Referência do Pedido do Seller.', zh: '卖家订单 ID / 参考号。' },
    saleDate: { en: 'Date when order was placed in ISO 8601 format.', es: 'Fecha en que se realizó la orden en formato ISO 8601.', pt: 'Data em que o pedido foi feito em formato ISO 8601.', zh: '订单下单日期,ISO 8601 格式。' },
    service: { en: 'Service code (e.g. CN0030MX, MXEXPMX, CLEXPCL). See SOP & ratecard.', es: 'Código de servicio (ej. CN0030MX, MXEXPMX, CLEXPCL). Ver SOP y ratecard.', pt: 'Código do serviço (ex. CN0030MX, MXEXPMX, CLEXPCL). Ver SOP e ratecard.', zh: '服务代码(例如 CN0030MX、MXEXPMX、CLEXPCL)。参见 SOP 和价目表。' },
    marketplaceCode: { en: 'Code of the marketplace where product was sold.', es: 'Código del marketplace donde se vendió el producto.', pt: 'Código do marketplace onde o produto foi vendido.', zh: '产品销售的电商平台代码。' },
    originWh: { en: 'Warehouse code. Default: Shenzhen. Miami: WH9001, Hong Kong: WH4350, Dongguan: WH3697, Jiaxing: WH4351.', es: 'Código del almacén. Default: Shenzhen. Miami: WH9001, Hong Kong: WH4350, Dongguan: WH3697, Jiaxing: WH4351.', pt: 'Código do armazém. Padrão: Shenzhen. Miami: WH9001, Hong Kong: WH4350, Dongguan: WH3697, Jiaxing: WH4351.', zh: '仓库代码。默认:深圳。迈阿密:WH9001,香港:WH4350,东莞:WH3697,嘉兴:WH4351。' },
    ddp: { en: 'Y = DDP service, N = DDU service.', es: 'Y = servicio DDP, N = servicio DDU.', pt: 'Y = serviço DDP, N = serviço DDU.', zh: 'Y = DDP 服务,N = DDU 服务。' },
    returnsAllowed: { en: 'Indicates if package has return allowed.', es: 'Indica si el paquete tiene devolución permitida.', pt: 'Indica se o pacote tem devolução permitida.', zh: '指示包裹是否允许退货。' },
    labelFormat: { en: 'Determines label format. Default: pdf.', es: 'Determina el formato de la etiqueta. Default: pdf.', pt: 'Determina o formato da etiqueta. Padrão: pdf.', zh: '决定面单格式。默认:pdf。' },

    // COD
    codAmount: { en: 'COD amount. Only for specific COD services.', es: 'Monto COD. Solo para servicios COD específicos.', pt: 'Valor COD. Apenas para serviços COD específicos.', zh: 'COD 金额。仅适用于特定 COD 服务。' },
    codCurrency: { en: 'Currency international code.', es: 'Código internacional de moneda.', pt: 'Código internacional de moeda.', zh: '国际货币代码。' },
    isCod: { en: 'True/false depending on whether the service is Cash on Delivery.', es: 'True/false según si el servicio es Cash on Delivery.', pt: 'True/false dependendo se o serviço é Cash on Delivery.', zh: 'True/false,取决于服务是否为货到付款。' },

    // Package
    netWeight: { en: 'Net weight in kilograms.', es: 'Peso neto en kilogramos.', pt: 'Peso líquido em quilogramas.', zh: '净重(千克)。' },
    weight: { en: 'Gross weight in kilograms.', es: 'Peso bruto en kilogramos.', pt: 'Peso bruto em quilogramas.', zh: '毛重(千克)。' },
    height: { en: 'Package height in centimeters.', es: 'Alto del paquete en centímetros.', pt: 'Altura do pacote em centímetros.', zh: '包裹高度(厘米)。' },
    width: { en: 'Package width in centimeters.', es: 'Ancho del paquete en centímetros.', pt: 'Largura do pacote em centímetros.', zh: '包裹宽度(厘米)。' },
    length: { en: 'Package length in centimeters.', es: 'Largo del paquete en centímetros.', pt: 'Comprimento do pacote em centímetros.', zh: '包裹长度(厘米)。' },
    declaredValue: { en: 'Package declared value (FOB) in USD.', es: 'Valor declarado del paquete (FOB) en USD.', pt: 'Valor declarado do pacote (FOB) em USD.', zh: '包裹申报价值(FOB),美元。' },
    insurance: { en: 'Package insurance value.', es: 'Valor del seguro del paquete.', pt: 'Valor do seguro do pacote.', zh: '包裹保险价值。' },
    cif: { en: 'Sum of FOB + insurance + freight.', es: 'Suma de FOB + seguro + flete.', pt: 'Soma de FOB + seguro + frete.', zh: 'FOB + 保险 + 运费的总和。' },
    duties: { en: 'Package duties value.', es: 'Valor de aranceles del paquete.', pt: 'Valor de impostos alfandegários do pacote.', zh: '包裹关税价值。' },
    taxes: { en: 'Package taxes value.', es: 'Valor de impuestos del paquete.', pt: 'Valor de impostos do pacote.', zh: '包裹税费价值。' },
    freight: { en: 'Package freight value.', es: 'Valor del flete del paquete.', pt: 'Valor do frete do pacote.', zh: '包裹运费价值。' },
    weightUnit: { en: 'kg, g, lb, oz.', es: 'kg, g, lb, oz.', pt: 'kg, g, lb, oz.', zh: 'kg、g、lb、oz。' },
    measurementUnit: { en: 'cm, in.', es: 'cm, in.', pt: 'cm, in.', zh: 'cm、in。' },
    pkgCurrency: { en: 'Currency international code.', es: 'Código internacional de moneda.', pt: 'Código internacional de moeda.', zh: '国际货币代码。' },
    exchangeRate: { en: 'Exchange rate for value conversion.', es: 'Tipo de cambio para conversión de valor.', pt: 'Taxa de câmbio para conversão de valor.', zh: '用于价值转换的汇率。' },
    ecommerceName: { en: 'E-commerce name for Brazil receita federal.', es: 'Nombre del e-commerce para receita federal de Brasil.', pt: 'Nome do e-commerce para receita federal do Brasil.', zh: '巴西联邦税务局所需的电商名称。' },
    senderCode: { en: 'Trader Information Number.', es: 'Trader Information Number.', pt: 'Trader Information Number.', zh: '商户信息编号(Trader Information Number)。' },
    exportCode: { en: 'Seller reference code used to export a package.', es: 'Código de referencia del seller para exportar un paquete.', pt: 'Código de referência do seller para exportar um pacote.', zh: '卖家用于出口包裹的参考代码。' },
    importCode: { en: 'Seller reference code used to import a package.', es: 'Código de referencia del seller para importar un paquete.', pt: 'Código de referência do seller para importar um pacote.', zh: '卖家用于进口包裹的参考代码。' },
    exportDeclaredValue: { en: 'Declared value to be presented in export documents.', es: 'Valor declarado a presentar en documentos de exportación.', pt: 'Valor declarado a apresentar em documentos de exportação.', zh: '出口文件中申报的价值。' },
    exportDeclaredCurrency: { en: 'Currency declared for export documents.', es: 'Moneda declarada para documentos de exportación.', pt: 'Moeda declarada para documentos de exportação.', zh: '出口文件中申报的货币。' },

    // Shipper / Buyer
    shipperName: { en: 'Shipper full name.', es: 'Nombre completo del remitente.', pt: 'Nome completo do remetente.', zh: '发货人全名。' },
    shipperAddr1: { en: 'Shipper address.', es: 'Dirección del remitente.', pt: 'Endereço do remetente.', zh: '发货人地址。' },
    addr2: { en: 'Main details about address.', es: 'Detalles principales de la dirección.', pt: 'Detalhes principais do endereço.', zh: '地址的主要详情。' },
    cityDash: { en: '—', es: '—', pt: '—', zh: '—' },
    shipperPostal: { en: 'Shipper postal code.', es: 'Código postal del remitente.', pt: 'CEP do remetente.', zh: '发货人邮政编码。' },
    isoCountry: { en: 'ISO 3166 country code.', es: 'Código de país ISO 3166.', pt: 'Código do país ISO 3166.', zh: 'ISO 3166 国家代码。' },
    shipperPhone: { en: 'Shipper phone.', es: 'Teléfono del remitente.', pt: 'Telefone do remetente.', zh: '发货人电话。' },

    buyerName: { en: 'Buyer full name.', es: 'Nombre completo del comprador.', pt: 'Nome completo do comprador.', zh: '买家全名。' },
    buyerId: { en: 'Buyer ID. Mandatory for BR & CL.', es: 'ID del comprador. Obligatorio para BR y CL.', pt: 'ID do comprador. Obrigatório para BR e CL.', zh: '买家 ID。巴西和智利必填。' },
    buyerCompany: { en: 'Company name.', es: 'Nombre de la empresa.', pt: 'Nome da empresa.', zh: '公司名称。' },
    buyerAddr1: { en: 'Buyer address.', es: 'Dirección del comprador.', pt: 'Endereço do comprador.', zh: '买家地址。' },
    buyerAddrNum: { en: 'Address number. Mandatory for BR packet standard, express & mini.', es: 'Número de la dirección. Obligatorio para BR packet standard, express y mini.', pt: 'Número do endereço. Obrigatório para BR packet standard, express e mini.', zh: '地址门牌号。巴西 packet standard、express 和 mini 必填。' },
    buyerPostal: { en: 'Buyer postal code.', es: 'Código postal del comprador.', pt: 'CEP do comprador.', zh: '买家邮政编码。' },
    buyerPhone: { en: 'Buyer phone.', es: 'Teléfono del comprador.', pt: 'Telefone do comprador.', zh: '买家电话。' },
    bornAt: { en: 'Buyer birthdate. Format YYYY-MM-DD.', es: 'Fecha de nacimiento del comprador. Formato YYYY-MM-DD.', pt: 'Data de nascimento do comprador. Formato YYYY-MM-DD.', zh: '买家出生日期。格式 YYYY-MM-DD。' },
    emailMax: { en: '—', es: '—', pt: '—', zh: '—' },

    // Items
    itemQty: { en: 'Quantity of item in the package.', es: 'Cantidad de ítems en el paquete.', pt: 'Quantidade de itens no pacote.', zh: '包裹中的商品数量。' },
    itemSku: { en: '—', es: '—', pt: '—', zh: '—' },
    itemDesc: { en: 'Short description of the product.', es: 'Descripción corta del producto.', pt: 'Descrição curta do produto.', zh: '产品简短描述。' },
    itemDescAlt: { en: 'Translation of product description in origin country language.', es: 'Traducción de la descripción del producto al idioma del país de origen.', pt: 'Tradução da descrição do produto no idioma do país de origem.', zh: '产品描述的原产国语言翻译。' },
    itemNetWeight: { en: 'Item net weight.', es: 'Peso neto del ítem.', pt: 'Peso líquido do item.', zh: '商品净重。' },
    itemDeclared: { en: 'Declared item value in USD.', es: 'Valor declarado del ítem en USD.', pt: 'Valor declarado do item em USD.', zh: '商品申报价值,美元。' },
    hsCode: { en: 'HS Code used to classify a product.', es: 'HS Code utilizado para clasificar un producto.', pt: 'HS Code utilizado para classificar um produto.', zh: '用于商品分类的 HS Code。' },
    category: { en: 'Product category.', es: 'Categoría del producto.', pt: 'Categoria do produto.', zh: '产品类别。' },
    dash: { en: '—', es: '—', pt: '—', zh: '—' },
  };
  const d = (key) => D[key]?.[language] || D[key]?.en || '—';

  const PREALERT_FIELDS = [
    { name: 'name', required: true, type: 'STRING', example: '"Name LastName"', description: d('paName') },
    { name: 'email', required: true, type: 'STRING', example: '"example@example.com"', description: d('paEmail') },
    { name: 'Warehouse', required: true, type: 'STRING', example: '"Miami"', description: d('paWarehouse') },
    { name: 'FM_carrier', required: true, type: 'STRING', example: '"FEDEX"', description: d('paFMcarrier') },
    { name: 'tracking_FM', required: true, type: 'STRING', example: '"01010101"', description: d('paTrackingFM') },
    { name: 'ETA', required: true, type: 'DATE', example: '"2025-10-08"', description: d('paETA') },
    { name: 'date', required: true, type: 'DATE', example: '"2025-10-08"', description: d('paDate') },
    { name: 'tracking_numbers', required: true, type: 'ARRAY', example: '["MLAR000000000EX", ...]', description: d('paTrackingNumbers') },
  ];

  const LABEL_FIELDS = {
    general: [
      { name: 'order_id', required: 'Yes', type: 'String', description: d('orderId') },
      { name: 'sale_date', required: 'Yes', type: 'String', description: d('saleDate') },
      { name: 'service', required: 'Yes', type: 'String', description: d('service') },
      { name: 'marketplace_code', required: 'No', type: 'String', description: d('marketplaceCode') },
      { name: 'origin_warehouse_code', required: 'No', type: 'String (max 6)', description: d('originWh') },
      { name: 'delivery_duties_paid', required: 'No', type: '"Y" or "N"', description: d('ddp') },
      { name: 'returns_allowed', required: 'No', type: '"Y" or "N"', description: d('returnsAllowed') },
      { name: 'label_format', required: 'No', type: '"pdf" or "zpl"', description: d('labelFormat') },
    ],
    cod: [
      { name: 'amount', required: 'No', type: 'Number', description: d('codAmount') },
      { name: 'currency', required: 'No', type: 'String', description: d('codCurrency') },
      { name: 'isCod', required: 'No', type: 'Boolean', description: d('isCod') },
    ],
    package: [
      { name: 'net_weight', required: 'No', type: 'Number', description: d('netWeight') },
      { name: 'weight', required: 'Yes', type: 'Number', description: d('weight') },
      { name: 'height', required: 'No', type: 'Number', description: d('height') },
      { name: 'width', required: 'No', type: 'Number', description: d('width') },
      { name: 'length', required: 'No', type: 'Number', description: d('length') },
      { name: 'declared_value', required: 'No', type: 'Number', description: d('declaredValue') },
      { name: 'insurance', required: 'No', type: 'Number', description: d('insurance') },
      { name: 'cif', required: 'No', type: 'Number', description: d('cif') },
      { name: 'duties', required: 'No', type: 'Number', description: d('duties') },
      { name: 'taxes', required: 'No', type: 'Number', description: d('taxes') },
      { name: 'freight', required: 'No', type: 'Number', description: d('freight') },
      { name: 'weight_unit', required: 'No', type: 'String', description: d('weightUnit') },
      { name: 'measurement_unit', required: 'No', type: 'String', description: d('measurementUnit') },
      { name: 'currency', required: 'Yes (PRC)', type: 'String', description: d('pkgCurrency') },
      { name: 'exchange_rate', required: 'Yes (PRC/USD)', type: 'Number', description: d('exchangeRate') },
      { name: 'ecommerce_name', required: 'Yes (PRC)', type: 'String', description: d('ecommerceName') },
      { name: 'sender_code_ece', required: 'Yes (PRC)', type: 'String', description: d('senderCode') },
      { name: 'export_code', required: 'No', type: 'String', description: d('exportCode') },
      { name: 'import_code', required: 'No', type: 'String', description: d('importCode') },
      { name: 'export_declared_value', required: 'No', type: 'String', description: d('exportDeclaredValue') },
      { name: 'export_declared_currency', required: 'No', type: 'String', description: d('exportDeclaredCurrency') },
    ],
    shipper: [
      { name: 'name', required: 'No', type: 'String', description: d('shipperName') },
      { name: 'address1', required: 'Yes', type: 'String', description: d('shipperAddr1') },
      { name: 'address2', required: 'No', type: 'String', description: d('addr2') },
      { name: 'city', required: 'Yes', type: 'String', description: d('cityDash') },
      { name: 'state', required: 'Yes', type: 'String', description: d('cityDash') },
      { name: 'postal_code', required: 'No', type: 'String', description: d('shipperPostal') },
      { name: 'country', required: 'Yes', type: 'String', description: d('isoCountry') },
      { name: 'phone', required: 'No', type: 'String', description: d('shipperPhone') },
      { name: 'email', required: 'No', type: 'String (max 100)', description: d('emailMax') },
    ],
    buyer: [
      { name: 'name', required: 'Yes', type: 'String', description: d('buyerName') },
      { name: 'buyer_id', required: 'No', type: 'String', description: d('buyerId') },
      { name: 'company', required: 'No', type: 'String', description: d('buyerCompany') },
      { name: 'address1', required: 'Yes', type: 'String', description: d('buyerAddr1') },
      { name: 'address1_number', required: 'No', type: 'Number', description: d('buyerAddrNum') },
      { name: 'city', required: 'Yes', type: 'String', description: d('cityDash') },
      { name: 'state', required: 'Yes', type: 'String', description: d('cityDash') },
      { name: 'postal_code', required: 'No', type: 'String', description: d('buyerPostal') },
      { name: 'country', required: 'Yes', type: 'String', description: d('isoCountry') },
      { name: 'phone', required: 'No', type: 'String', description: d('buyerPhone') },
      { name: 'email', required: 'No', type: 'String (max 100)', description: d('emailMax') },
      { name: 'born_at', required: 'No', type: 'Date', description: d('bornAt') },
    ],
    items: [
      { name: 'quantity', required: 'Yes', type: 'Number', description: d('itemQty') },
      { name: 'sku', required: 'No', type: 'String (max 255)', description: d('itemSku') },
      { name: 'description', required: 'Yes', type: 'String', description: d('itemDesc') },
      { name: 'description_alt', required: 'No', type: 'String', description: d('itemDescAlt') },
      { name: 'net_weight', required: 'No', type: 'Number', description: d('itemNetWeight') },
      { name: 'declared_value', required: 'No', type: 'Number', description: d('itemDeclared') },
      { name: 'hs_code', required: 'No', type: 'String', description: d('hsCode') },
      { name: 'Category', required: 'No', type: 'String', description: d('category') },
      { name: 'product_url', required: 'No', type: 'String', description: d('dash') },
    ],
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(PREALERT_EXAMPLE);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const labelGroups = [
    { id: 'general', label: t('apiDocs.label.tabs.general'), icon: Info },
    { id: 'package', label: t('apiDocs.label.tabs.package'), icon: Package },
    { id: 'shipper', label: t('apiDocs.label.tabs.shipper'), icon: Server },
    { id: 'buyer', label: t('apiDocs.label.tabs.buyer'), icon: Shield },
    { id: 'items', label: t('apiDocs.label.tabs.items'), icon: Tag },
    { id: 'cod', label: t('apiDocs.label.tabs.cod'), icon: Key },
  ];

  return (
    <section
      id="api-docs"
      data-testid="api-docs-section"
      className="py-24 md:py-32 bg-[#050505] relative overflow-hidden"
    >
      {/* Decorative grid */}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none"
           style={{ backgroundImage: 'linear-gradient(#7AC142 1px, transparent 1px), linear-gradient(90deg, #7AC142 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="eyebrow mb-4">
            <Code2 className="w-4 h-4" />
            {t('apiDocs.badge')}
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight">
            {t('apiDocs.title')} <span className="text-[#7AC142]">{t('apiDocs.titleAccent')}</span>
          </h2>
          <p className="mt-4 text-gray-400 max-w-3xl mx-auto text-lg">
            {t('apiDocs.subtitle')}
          </p>
        </motion.div>

        {/* API Switch */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex bg-black/50 border border-white/10 p-1">
            <button
              onClick={() => setActiveApi('label')}
              className={`flex items-center gap-2 px-3 sm:px-6 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all ${
                activeApi === 'label' ? 'bg-[#7AC142] text-black' : 'text-white/60 hover:text-white'
              }`}
              data-testid="api-tab-label"
            >
              <FileCode className="w-4 h-4" />
              {t('apiDocs.label.tab')}
            </button>
            <button
              onClick={() => setActiveApi('prealert')}
              className={`flex items-center gap-2 px-3 sm:px-6 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider transition-all ${
                activeApi === 'prealert' ? 'bg-[#7AC142] text-black' : 'text-white/60 hover:text-white'
              }`}
              data-testid="api-tab-prealert"
            >
              <Zap className="w-4 h-4" />
              {t('apiDocs.prealert.tab')}
            </button>
          </div>
        </div>

        {/* PRE-ALERT API */}
        {activeApi === 'prealert' && (
          <motion.div
            key="prealert"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 lg:grid-cols-5 gap-8"
            data-testid="prealert-api-panel"
          >
            {/* Left: description + fields */}
            <div className="lg:col-span-3 space-y-6 min-w-0">
              <div className="bg-white/5 border border-white/10 p-6">
                <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                  <Zap className="w-6 h-6 text-[#7AC142]" />
                  {t('apiDocs.prealert.heading')}
                </h3>
                <p className="text-gray-400 leading-relaxed mb-4">
                  {t('apiDocs.prealert.description')}
                </p>
                <div className="flex items-start gap-3 bg-[#7AC142]/5 border border-[#7AC142]/30 p-4">
                  <Key className="w-5 h-5 text-[#7AC142] shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-300">
                    <span className="font-bold text-[#7AC142]">{t('apiDocs.prealert.salesNoteLabel')}:</span>{' '}
                    {t('apiDocs.prealert.salesNote')}
                  </p>
                </div>
              </div>

              {/* Fields table */}
              <div className="bg-black/40 border border-white/10 overflow-hidden">
                <div className="px-6 py-4 border-b border-white/10 bg-white/5">
                  <h4 className="font-bold text-white uppercase tracking-wider text-sm">{t('apiDocs.prealert.requestBody')}</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 text-gray-400 uppercase text-xs">
                      <tr>
                        <th className="px-4 py-3 text-left font-mono">{t('apiDocs.table.field')}</th>
                        <th className="px-4 py-3 text-left">{t('apiDocs.table.type')}</th>
                        <th className="px-4 py-3 text-left">{t('apiDocs.table.required')}</th>
                        <th className="px-4 py-3 text-left">{t('apiDocs.table.description')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {PREALERT_FIELDS.map((field) => (
                        <tr key={field.name} className="hover:bg-white/5">
                          <td className="px-4 py-3 font-mono text-[#7AC142] font-bold">{field.name}</td>
                          <td className="px-4 py-3 text-gray-300">{field.type}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-red-500/10 text-red-400 text-xs font-bold uppercase">
                              {field.required ? t('apiDocs.table.requiredYes') : t('apiDocs.table.requiredNo')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-400">{field.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Response codes */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-emerald-500/5 border border-emerald-500/20 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-emerald-400 rounded-full" />
                    <span className="text-xs uppercase tracking-wider text-emerald-400 font-bold">{t('apiDocs.response.success')}</span>
                  </div>
                  <p className="text-2xl font-mono text-white">200 OK</p>
                  <p className="text-xs text-gray-400 mt-1">{t('apiDocs.response.successNote')}</p>
                </div>
                <div className="bg-red-500/5 border border-red-500/20 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 bg-red-400 rounded-full" />
                    <span className="text-xs uppercase tracking-wider text-red-400 font-bold">{t('apiDocs.response.error')}</span>
                  </div>
                  <p className="text-2xl font-mono text-white">400 Bad Request</p>
                  <p className="text-xs text-gray-400 mt-1">{t('apiDocs.response.errorNote')}</p>
                </div>
              </div>
            </div>

            {/* Right: code example */}
            <div className="lg:col-span-2 min-w-0">
              <div className="bg-black border border-white/10 sticky top-24">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/5">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500/60" />
                      <div className="w-3 h-3 rounded-full bg-yellow-500/60" />
                      <div className="w-3 h-3 rounded-full bg-green-500/60" />
                    </div>
                    <span className="ml-2 text-xs font-mono text-gray-400">{t('apiDocs.prealert.exampleTitle')}</span>
                  </div>
                  <button
                    onClick={handleCopy}
                    className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#7AC142] transition-colors"
                    data-testid="copy-prealert-example"
                  >
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? t('apiDocs.copied') : t('apiDocs.copy')}
                  </button>
                </div>
                <pre className="p-4 text-xs font-mono text-gray-300 overflow-x-auto leading-relaxed">
                  <code>{PREALERT_EXAMPLE}</code>
                </pre>
              </div>
            </div>
          </motion.div>
        )}

        {/* LABEL GENERATOR API */}
        {activeApi === 'label' && (
          <motion.div
            key="label"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
            data-testid="label-api-panel"
          >
            {/* Description */}
            <div className="bg-white/5 border border-white/10 p-6">
              <h3 className="text-2xl font-bold text-white mb-3 flex items-center gap-2">
                <FileCode className="w-6 h-6 text-[#7AC142]" />
                {/* Headings are uppercased: the version keeps its real case */}
                <span>
                  {t('apiDocs.label.heading').split('v1.0')[0]}
                  <span className="normal-case">v1.0</span>
                </span>
              </h3>
              <p className="text-gray-400 leading-relaxed mb-4">
                {t('apiDocs.label.description')}
              </p>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="bg-black/40 border border-white/10 p-3 text-center">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{t('apiDocs.label.method')}</p>
                  <p className="font-mono font-bold text-[#7AC142]">POST</p>
                </div>
                <div className="bg-black/40 border border-white/10 p-3 text-center">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{t('apiDocs.label.formats')}</p>
                  <p className="font-mono font-bold text-white">PDF / ZPL</p>
                </div>
                <div className="bg-black/40 border border-white/10 p-3 text-center">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">{t('apiDocs.label.envs')}</p>
                  <p className="font-mono font-bold text-white">QA / PROD</p>
                </div>
              </div>
              <div className="flex items-start gap-3 mt-4 bg-[#7AC142]/5 border border-[#7AC142]/30 p-4">
                <Key className="w-5 h-5 text-[#7AC142] shrink-0 mt-0.5" />
                <p className="text-sm text-gray-300">
                  <span className="font-bold text-[#7AC142]">{t('apiDocs.prealert.salesNoteLabel')}:</span>{' '}
                  {t('apiDocs.label.salesNote')}
                </p>
              </div>
            </div>

            {/* Field group tabs */}
            <div className="flex flex-wrap gap-2">
              {labelGroups.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveLabelGroup(id)}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-bold uppercase tracking-wider transition-all border ${
                    activeLabelGroup === id
                      ? 'bg-[#7AC142] text-black border-[#7AC142]'
                      : 'text-white/60 border-white/10 hover:border-white/30'
                  }`}
                  data-testid={`label-group-${id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>

            {/* Fields table */}
            <div className="bg-black/40 border border-white/10 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-gray-400 uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 text-left font-mono">{t('apiDocs.table.field')}</th>
                      <th className="px-4 py-3 text-left">{t('apiDocs.table.type')}</th>
                      <th className="px-4 py-3 text-left">{t('apiDocs.table.required')}</th>
                      <th className="px-4 py-3 text-left">{t('apiDocs.table.description')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {LABEL_FIELDS[activeLabelGroup].map((field, i) => (
                      <tr key={`${field.name}-${i}`} className="hover:bg-white/5">
                        <td className="px-4 py-3 font-mono text-[#7AC142] font-bold whitespace-nowrap">{field.name}</td>
                        <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{field.type}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-0.5 text-xs font-bold uppercase ${
                            field.required === 'Yes' || field.required.startsWith('Yes')
                              ? 'bg-red-500/10 text-red-400'
                              : 'bg-gray-500/10 text-gray-400'
                          }`}>
                            {field.required.replace(/^Yes/, t('apiDocs.table.requiredYes')).replace(/^No$/, t('apiDocs.table.requiredNo'))}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400">{field.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Special validations / annexes */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white/5 border border-white/10 p-5">
                <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-[#7AC142]" />
                  {t('apiDocs.label.annex.brazil.title')}
                </h4>
                <ul className="text-sm text-gray-400 space-y-2">
                  <AnnexItems items={t('apiDocs.label.annex.brazil.items')} />
                </ul>
              </div>
              <div className="bg-white/5 border border-white/10 p-5">
                <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-[#7AC142]" />
                  {t('apiDocs.label.annex.others.title')}
                </h4>
                <ul className="text-sm text-gray-400 space-y-2">
                  <AnnexItems items={t('apiDocs.label.annex.others.items')} />
                </ul>
              </div>
            </div>

            {/* Business logic */}
            <div className="bg-gradient-to-br from-[#7AC142]/5 to-transparent border border-[#7AC142]/20 p-6">
              <h4 className="font-bold text-white mb-3 flex items-center gap-2">
                <Info className="w-5 h-5 text-[#7AC142]" />
                {t('apiDocs.label.businessLogic.title')}
              </h4>
              <p className="text-sm text-gray-300 mb-3">
                {t('apiDocs.label.businessLogic.description')}
              </p>
              <ul className="text-sm text-gray-400 space-y-1">
                <li>• <span className="text-white">{t('apiDocs.label.businessLogic.value')}:</span> {t('apiDocs.label.businessLogic.valueDesc')}</li>
                <li>• <span className="text-white">{t('apiDocs.label.businessLogic.weight')}:</span> {t('apiDocs.label.businessLogic.weightDesc')}</li>
              </ul>
            </div>
          </motion.div>
        )}

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center bg-gradient-to-r from-[#7AC142]/10 via-[#7AC142]/5 to-transparent border border-[#7AC142]/20 p-8"
        >
          <h3 className="text-2xl font-bold text-white mb-3">{t('apiDocs.cta.title')}</h3>
          <p className="text-gray-400 mb-6 max-w-2xl mx-auto">{t('apiDocs.cta.description')}</p>
          <a
            href={QUOTE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#7AC142] text-black px-8 py-3 text-sm font-bold uppercase tracking-wider hover:bg-[#69A835] transition-colors"
            data-testid="api-docs-cta"
          >
            {t('apiDocs.cta.button')}
            <ChevronRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ApiDocsSection;
